import React, { useCallback, useMemo, useState } from 'react';
import {
  AU_STATES,
  CURRENCIES,
  DEAL_ROOM_TOTAL_PACKAGE_GST,
  NZ_REGIONS,
  STOCK_AT_VALUE,
  DealRoomLocation,
} from 'models/DealRoom';
import { useTranslation } from 'react-i18next';
import useCreateSelectOptions from 'hooks/useCreateSelectOptions';
import { getHasElementsInArray } from 'utils';
import { Option } from 'models/Global';

export default function useFirstStepValues() {
  const { t } = useTranslation();

  const createSelectOptions = useCreateSelectOptions();

  const [locationSearchTerm, setLocationSearchTerm] = useState('');

  const onLocationSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) =>
      setLocationSearchTerm(e.target.value),
    [],
  );

  const auOptions = useMemo(
    () => createSelectOptions(Object.values(AU_STATES)),
    [createSelectOptions],
  );

  const nzOptions = useMemo(
    () => createSelectOptions(Object.values(NZ_REGIONS)),
    [createSelectOptions],
  );

  const filterByTerm = useCallback(
    (options: Option[]) => {
      const term = locationSearchTerm.trim().toLowerCase();

      return term
        ? options.filter(({ label }) => label.toLowerCase().includes(term))
        : options;
    },
    [locationSearchTerm],
  );

  const dealLocationOptions = useMemo(() => {
    const filteredAu = filterByTerm(auOptions);
    const filteredNz = filterByTerm(nzOptions);

    return [
      ...(getHasElementsInArray(filteredAu)
        ? [
            { value: 'au', label: t('australia'), disabled: true },
            ...filteredAu,
          ]
        : []),
      ...(getHasElementsInArray(filteredNz)
        ? [
            { value: 'nz', label: t('newZealand'), disabled: true },
            ...filteredNz,
          ]
        : []),
    ];
  }, [auOptions, nzOptions, t, filterByTerm]);

  const currencyOptions = useMemo(
    () => [
      { value: CURRENCIES.AUD, label: t('$aud') },
      { value: CURRENCIES.NZD, label: t('$nzd') },
    ],
    [t],
  );

  const gstOptions = useMemo(
    () => createSelectOptions(Object.values(DEAL_ROOM_TOTAL_PACKAGE_GST)),
    [createSelectOptions],
  );

  const includesSAVOptions = useMemo(
    () => [
      {
        label: t('excludesStock'),
        value: STOCK_AT_VALUE.EXCLUDE_STOCK_AT_VALUE,
      },
      {
        label: t('includesStock'),
        value: STOCK_AT_VALUE.INCLUDE_STOCK_AT_VALUE,
      },
      { label: t('noStock'), value: STOCK_AT_VALUE.NO_STOCK_AT_VALUE },
    ],
    [t],
  );

  const checkIfLocationIsAU = useCallback(
    (location: DealRoomLocation) =>
      Object.values(AU_STATES).some((el) => el === location),
    [],
  );

  return {
    gstOptions,
    dealLocationOptions,
    currencyOptions,
    includesSAVOptions,
    checkIfLocationIsAU,
    onLocationSearchChange,
  };
}
